import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { baseUrl, userLocation, ipAddress, userName } from "../Univ.js";

function ProductionOrderPage() {

  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [WCs, setWCs] = useState([]);

  const [data, setData] = useState({
    order_no: "",
    product: "",
    quantity: "",
    start_date: "",
    end_date: "",
    workcenter: ""
  });

  useEffect(() => {
    fetchOrders();
    fetchProducts();
    fetchWCs();
  }, []);

  // Fetch existing production orders
  const fetchOrders = () => {
    axios.get(baseUrl + "production-orders/")
      .then(res => setRows(res.data))
      .catch(() => Swal.fire("Error", "Could not fetch Production Orders", "error"));
  };

  const fetchProducts = () => {
    axios.get(baseUrl + "products/")
      .then(res => setProducts(res.data))
      .catch(() => Swal.fire("Error", "Could not fetch Products", "error"));
  };

  const fetchWCs = () => {
    axios.get(baseUrl + "workcenters/")
      .then(res => setWCs(res.data))
      .catch(() => Swal.fire("Error", "Could not fetch Work Centers", "error"));
  };

  const inputHandler = (e) => {
    setData({ ...data, [e.target.name]: e.target.value });
  };

  const formSubmit = () => {
    if (!data.order_no || !data.product || !data.quantity || !data.start_date || !data.workcenter) {
      Swal.fire("Error", "Please fill all required fields", "error");
      return;
    }

    const _formData = new FormData();
    _formData.append("order_no", data.order_no);
    _formData.append("product", data.product);
    _formData.append("quantity", data.quantity);
    _formData.append("start_date", data.start_date);
    if (data.end_date) _formData.append("end_date", data.end_date);
    _formData.append("workcenter", data.workcenter);
    _formData.append("status", "1");
    _formData.append("addedby", userName);
    _formData.append("ip_add", ipAddress);
    _formData.append("location", userLocation);

    axios.post(baseUrl + "production-orders/", _formData, {
      headers: { "Content-Type": "multipart/form-data" }
    })
    .then(response => {
      if (response.status === 200 || response.status === 201) {
        Swal.fire({
          title: "Production Order Created",
          icon: "success",
          timer: 2000,
          showConfirmButton: false
        });
        setData({
          order_no: "",
          product: "",
          quantity: "",
          start_date: "",
          end_date: "",
          workcenter: ""
        });
        fetchOrders();
      }
    })
    .catch(() => Swal.fire("Error", "Something went wrong", "error"));
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: "Confirm",
      text: "Are you sure you want to delete this?",
      icon: "warning",
      confirmButtonText: "Yes",
      showCancelButton: true
    }).then(result => {
      if (result.isConfirmed) {
        axios.delete(baseUrl + "production-orders/" + id + "/")
          .then(() => {
            Swal.fire("Deleted", "Production Order deleted", "success");
            fetchOrders();
          });
      }
    });
  };

  const columns = [
    { field: "id", headerName: "ID", width: 80 },
    { field: "order_no", headerName: "Order No", flex: 1 },
    {
      field: "product",
      headerName: "Product",
      flex: 1,
      renderCell: (params) => params.row.product?.name || "Unknown"
    },
    { field: "quantity", headerName: "Quantity", flex: 1 },
    { field: "start_date", headerName: "Start Date", flex: 1 },
    { field: "end_date", headerName: "End Date", flex: 1 },
    {
      field: "workcenter",
      headerName: "Work Center",
      flex: 1,
      renderCell: (params) => params.row.workcenter?.name || "Unknown"
    },
    {
      field: "edit",
      headerName: "Edit",
      renderCell: (params) => (
        <Link
          to={`/production-orders/edit/${params.row.id}`}
          target="_blank"
          className="btn btn-warning btn-sm"
        >
          Edit
        </Link>
      ),
      width: 120
    },
    {
      field: "delete",
      headerName: "Delete",
      renderCell: (params) => (
        <button
          onClick={() => handleDelete(params.row.id)}
          className="btn btn-danger btn-sm"
        >
          Delete
        </button>
      ),
      width: 120
    }
  ];

  return (
    <div className="container mt-4">

      {/* Form */}
      <div className="card p-4 mb-4">
        <h4>Add Production Order</h4>

        <input
          type="text"
          className="form-control mt-3"
          placeholder="Order No"
          name="order_no"
          value={data.order_no}
          onChange={inputHandler}
        />

        <select
          className="form-control mt-3"
          name="product"
          value={data.product}
          onChange={inputHandler}
        >
          <option value="">Select Product</option>
          {products.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>

        <input
          type="number"
          className="form-control mt-3"
          placeholder="Quantity"
          name="quantity"
          value={data.quantity}
          onChange={inputHandler}
        />

        <input
          type="date"
          className="form-control mt-3"
          name="start_date"
          value={data.start_date}
          onChange={inputHandler}
        />

        <input
          type="date"
          className="form-control mt-3"
          name="end_date"
          value={data.end_date}
          onChange={inputHandler}
        />

        <select
          className="form-control mt-3"
          name="workcenter"
          value={data.workcenter}
          onChange={inputHandler}
        >
          <option value="">Select Work Center</option>
          {WCs.map(wc => (
            <option key={wc.id} value={wc.id}>{wc.name}</option>
          ))}
        </select>

        <button
          className="btn btn-success mt-3"
          onClick={formSubmit}
        >
          Submit
        </button>
      </div>

      {/* DataGrid */}
      <div className="card p-3">
        <DataGrid
          sx={{
            boxShadow: 4,
            border: 4,
            borderColor: "primary.light",
            "& .MuiDataGrid-cell:hover": { color: "primary.main" }
          }}
          rows={rows}
          columns={columns}
          pageSize={10}
          autoHeight
          components={{ Toolbar: GridToolbar }}
        />
      </div>

    </div>
  );
}

export default ProductionOrderPage;
